// ────────────────────────────────────────────────────────────────────────────
// What the Plan cockpit offers this organisation.
//
// Stored on the Directory `Organisation` as `tmsPlanFeaturesJson` and edited by
// Stratum support from the Plan Cockpit card on Central Configuration:
//
//   { "pools": { "driverRoster": true, "subcontractors": false }, "gantt": true }
//
// SAME RULE AS THE WORK VIEW TABS: A MISSING KEY MEANS ON. Null column,
// unreadable string, a pool this build has but the saved map has never heard
// of — all resolve to enabled, so a new pool never arrives switched off for an
// organisation that saved before it existed, and an un-configured org (every
// org today) gets the cockpit as shipped.
//
// UNLIKE the tabs there is no "at least one" rule, on the BE or here. Every
// pool off is a coherent setup for an organisation that assigns purely from the
// Gantt, and the strip already renders a bare "Resources" band in that case.
// ────────────────────────────────────────────────────────────────────────────
import { POOL_ORDER, type PoolId } from './planResources';

export interface PlanFeatures {
  /** Per-pool visibility for the resource strip. Every pool is present. */
  pools: Record<PoolId, boolean>;
  /** Whether the driver Gantt exists at all — section AND toolbar toggle. */
  gantt: boolean;
}

/** The cockpit as shipped: every pool, plus the Gantt. */
export function allPlanFeaturesEnabled(): PlanFeatures {
  return {
    pools: Object.fromEntries(POOL_ORDER.map((id) => [id, true])) as Record<PoolId, boolean>,
    gantt: true,
  };
}

/**
 * Read the stored document into a complete, every-pool-present record.
 *
 * <p>
 * Never throws and never returns a partial record. A malformed string is not
 * worth a broken cockpit — a planner would see an empty strip and no timeline,
 * indistinguishable from "support switched them off" — so anything unreadable
 * falls back to everything on, which is also what an un-configured org gets.
 * </p>
 */
export function parsePlanFeatures(json: string | null | undefined): PlanFeatures {
  const features = allPlanFeaturesEnabled();
  if (!json) return features;

  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return features;
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return features;

  const doc = raw as { pools?: unknown; gantt?: unknown };

  if (doc.pools && typeof doc.pools === 'object' && !Array.isArray(doc.pools)) {
    const stored = doc.pools as Record<string, unknown>;
    for (const id of POOL_ORDER) {
      // Only an explicit `false` takes a pool away. A non-boolean value is
      // treated as absent rather than as "off", on the same reasoning as above.
      if (stored[id] === false) features.pools[id] = false;
    }
  }

  if (doc.gantt === false) features.gantt = false;

  return features;
}

/**
 * The wire value. Always writes EVERY pool this build knows about rather than
 * only the disabled ones, so the stored document says what it means when
 * somebody reads the column directly, and a pool that is off is never confused
 * with one that did not exist when the row was written.
 */
export function serialisePlanFeatures(features: PlanFeatures): string {
  return JSON.stringify({
    pools: Object.fromEntries(POOL_ORDER.map((id) => [id, features.pools[id] !== false])),
    gantt: features.gantt !== false,
  });
}
